import { Check, LogOut, Settings, Users, X, School } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useParent from '../../../hooks/useParent.js';
import { colors, typography } from '../../../utils/styles';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown.tsx';
import { LoadingSpinner } from '../../ui/loading-spinner.tsx';
import ParentNotificationsDropdown from './ParentNotificationsDropdown.jsx';

// Mock notifications - we can move this to a proper data source later
// const notifications = [
//   {
//     icon: CreditCard,
//     title: 'Payment Received',
//     description: '₦50,000 - School Fees from John Doe (JSS 2A)',
//     time: '2 minutes ago',
//     iconColor: 'text-emerald-500',
//     iconBg: 'bg-emerald-50',
//     priority: 'high',
//   },
//   {
//     icon: Users,
//     title: 'New Student Enrollment',
//     description: 'Sarah Johnson has been enrolled in SSS 1B',
//     time: '57 minutes ago',
//     iconColor: 'text-blue-500',
//     iconBg: 'bg-blue-50',
//     priority: 'medium',
//   },
// ];

const getInitials = (name, email) => {
  if (name) {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  return email ? email[0].toUpperCase() : '?';
};

export default function ParentHeader({
  searchQuery,
  setSearchQuery,
  title,
  subtitle,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  const {
    parent,
    parentLogout,
    isParentLoggingOut,
    isParentLoading,
    refetchParent,
  } = useParent();
  const { currentChild, removeCurrentChild } = useParent();

  const navigate = useNavigate();

  const handleLogout = () => {
    removeCurrentChild();
    parentLogout(undefined, {
      onSuccess: () => {
        navigate('/parent/login', { replace: true });
        // Todo: Replace dispatch setisAuth to false with a more appropriate action
      },
      onError: (error) => {
        toast.error(error.message || 'Logout Failed! Please try again');
      },
    });
  };

  const handleVerifyEmail = () => {
    if (!parent?.email) {
      toast.error('No email address found');
      return;
    }

    // Just navigate to OTP page with email and fromDashboard flag
    navigate('/parent/otp', {
      state: {
        email: parent.email,
        fromDashboard: true,
      },
    });
  };

  const handleSearchToggle = () => {
    setIsSearchExpanded(!isSearchExpanded);
    if (!isSearchExpanded) {
      // Focus the input when expanding
      setTimeout(() => {
        document.querySelector('input[type="search"]')?.focus();
      }, 100);
    } else {
      // Clear search when collapsing
      setSearchQuery('');
    }
  };

  useEffect(() => {
    if (!parent && !isParentLoading) {
      refetchParent();
    }
  }, [parent, isParentLoading, refetchParent]);

  if (!parent) {
    return <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />;
  }

  return (
    // <header className=" rounded-lg h-[10vh]">
    <header className=" rounded-lg h-[10vh] fixed top-0 right-0 bg-[#F4F2F9] w-[100vw] md:w-auto z-10">
      <div className="px-8 h-full flex items-center justify-between">
        <div className="flex-1">
          <h1 className={`${typography.h1} ${colors.text.primary}`}>{title}</h1>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>

        <div className="flex items-center space-x-6">
          {/* Disable search for now */}
          {/* <div
            className={cn(
              'relative transition-all duration-300 ease-in-out',
              isSearchExpanded ? 'w-[320px]' : 'w-[40px]'
            )}
          >
            <div className="relative w-full">
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  'absolute left-0 top-0 h-full w-10 flex items-center justify-center',
                  isSearchExpanded
                    ? 'bg-transparent'
                    : 'bg-white hover:bg-gray-100 w-full rounded-lg'
                )}
                onClick={handleSearchToggle}
              >
                <Search className="h-4 w-4 text-gray-500" />
              </Button>
              <Input
                type="search"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={cn(
                  'pl-10 w-full bg-white/50 focus-visible:ring-purple-500 transition-all duration-300',
                  isSearchExpanded
                    ? 'opacity-100'
                    : 'opacity-0 pointer-events-none'
                )}
              />
              {isSearchExpanded && searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full w-10 flex items-center justify-center"
                  onClick={() => setSearchQuery('')}
                >
                  <X className="h-4 w-4 text-gray-500" />
                </Button>
              )}
            </div>
          </div> */}

          <ParentNotificationsDropdown />

          <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger
              className="flex items-center space-x-3 focus:outline-none disabled:opacity-50"
              disabled={isParentLoggingOut}
            >
              <Avatar className="h-10 w-10">
                <AvatarImage src={parent?.photoUrl || ''} />
                <AvatarFallback className="bg-purple-100 text-purple-700">
                  {getInitials(
                    `${parent?.firstName} ${parent?.lastName}`,
                    parent?.email
                  )}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:block text-left">
                <p className={`${typography.label} ${colors.text.primary}`}>
                  {parent?.firstName && parent?.lastName
                    ? `${parent.firstName} ${parent.lastName}`
                    : 'Loading name...'}
                </p>
                <p className={`${typography.small} ${colors.text.muted}`}>
                  {(parent?.baseRole || 'parent')[0].toUpperCase() +
                    (parent?.baseRole || 'parent').slice(1)}
                </p>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64 mt-1 p-1" align="end">
              <div className="flex flex-col px-3 py-2">
                <div className="flex items-center ">
                  <p className="text-sm font-medium text-gray-900">
                    {`${parent.firstName} ${parent.lastName}`}
                  </p>
                  {parent.verified ? (
                    <>
                      <Check className="w-4 h-4 ml-1.5 text-green-500" />
                      <p className="text-xs text-green-800">Verified</p>
                    </>
                  ) : (
                    <>
                      <X className="w-4 h-4 ml-1.5 text-red-500" />
                      <p className="text-xs text-red-800">Not Verified</p>
                    </>
                  )}
                </div>
                <p className={`${typography.small} ${colors.text.muted}`}>
                  {parent?.roleName || 'Parent'}
                </p>
              </div>
              {currentChild && (
                <div className="flex items-center space-x-3 px-3 py-2 mt-1 rounded-md bg-gray-100">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={currentChild?.photoURL || ''} />
                    <AvatarFallback className="bg-purple-100 text-purple-700">
                      {getInitials(
                        `${currentChild.firstName} ${currentChild.lastName}`,
                        ''
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col text-sm">
                    <span className="font-medium text-gray-900">{`${currentChild.firstName} ${currentChild.lastName}`}</span>
                    <span className="text-xs text-gray-600">
                      {currentChild?.status || 'N/A'}
                    </span>
                  </div>
                </div>
              )}
              <DropdownMenuSeparator />
              {parent?.verified ? (
                ''
              ) : (
                <DropdownMenuItem
                  className={`flex items-center px-3 py-2 text-sm cursor-pointer ${
                    colors.primary.main
                  }
                      ${
                        !isParentLoggingOut
                          ? `${colors.primary.hover} hover:bg-purple-50`
                          : 'opacity-50 cursor-not-allowed'
                      }`}
                  onSelect={(e) => {
                    e.preventDefault();
                    handleVerifyEmail();
                  }}
                  disabled={isParentLoggingOut}
                >
                  Verify Email
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="flex items-center px-3 py-2 text-sm cursor-pointer"
                onSelect={(e) => {
                  e.preventDefault();
                  navigate('/parent/settings', {
                    state: { label: 'Account Information' },
                  });
                }}
              >
                <Settings className="w-4 h-4 mr-2" /> Profile Settings
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex items-center px-3 py-2 text-sm cursor-pointer"
                onSelect={(e) => {
                  e.preventDefault();
                  navigate('/parent/select-child');
                }}
              >
                <School className="w-4 h-4 mr-2" /> Switch Child Profile
              </DropdownMenuItem>
              {/* <DropdownMenuItem
                className="flex items-center px-3 py-2 text-sm"
                onSelect={(e) => e.preventDefault()}
              >
                <CreditCard className="w-4 h-4 mr-2" /> Billing
              </DropdownMenuItem> */}
              <DropdownMenuItem
                className="flex items-center px-3 py-2 text-sm cursor-pointer"
                onSelect={(e) => e.preventDefault()}
              >
                <Users className="w-4 h-4 mr-2" /> Team
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className={`flex items-center px-3 py-2 text-sm text-red-600 cursor-pointer
                    ${
                      !isParentLoggingOut
                        ? 'hover:text-red-700 hover:bg-red-50'
                        : 'opacity-50 cursor-not-allowed'
                    }`}
                onSelect={(e) => {
                  e.preventDefault();
                  if (!isParentLoggingOut) handleLogout();
                }}
                disabled={isParentLoggingOut}
              >
                <div className="flex items-center w-full">
                  {isParentLoggingOut ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      <span>Logging out...</span>
                    </>
                  ) : (
                    <>
                      <LogOut className="w-4 h-4 mr-2" />
                      <span>Logout</span>
                    </>
                  )}
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
